/**
 * GenericAdDisplay Component
 *
 * Fallback display for platforms without specific mockups
 * Shows ad data in a clean, readable format
 */

import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { RichMessageText } from '@/components/chat/RichMessageText';
import { GenericAdDisplayProps } from '../types/platform.types';

export const GenericAdDisplay: React.FC<GenericAdDisplayProps> = ({
  ad,
  conversationMedia,
}) => {
  // Extract media URLs from media_tags
  const mediaUrls = ad.media_tags?.map((tag: string) => {
    const match = tag.match(/\[MEDIA:([^\|]+)\|/);
    if (match) {
      const filename = match[1];
      const media = conversationMedia.find(m => m.filename === filename);
      return media?.url;
    }
    return null;
  }).filter(Boolean) || [];

  return (
    <div className="p-4 bg-gray-50 dark:bg-background-dark rounded-lg border border-gray-200 dark:border-border-dark">
      {/* Format Badge */}
      {ad.format && (
        <div className="mb-3">
          <Badge variant="outline" size="sm">
            {ad.format.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
      )}

      {/* Media Preview */}
      {mediaUrls.length > 0 && (
        <div className="mb-3 grid grid-cols-2 gap-2">
          {mediaUrls.slice(0, 4).map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Media ${i + 1}`}
              className="w-full h-32 object-cover rounded-lg border border-gray-300 dark:border-border-dark"
            />
          ))}
        </div>
      )}

      {/* Headlines */}
      {ad.headlines && ad.headlines.length > 0 && (
        <div className="mb-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Headlines ({ad.headlines.length}):
          </p>
          <div className="space-y-1">
            {ad.headlines.slice(0, 3).map((headline: string, i: number) => (
              <div key={i} className="text-sm text-gray-700 dark:text-gray-300 pl-4 border-l-2 border-primary/30">
                <RichMessageText text={headline} uploadedMedia={conversationMedia} />
              </div>
            ))}
            {ad.headlines.length > 3 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 pl-4">
                +{ad.headlines.length - 3} more headlines
              </p>
            )}
          </div>
        </div>
      )}

      {/* Primary Text */}
      {ad.primary_text && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Ad Copy{Array.isArray(ad.primary_text) && ad.primary_text.length > 1 ? ` (${ad.primary_text.length} variations)` : ''}:
          </p>
          {Array.isArray(ad.primary_text) ? (
            <div className="space-y-2">
              {ad.primary_text.slice(0, 2).map((text: string, i: number) => (
                <div key={i} className="text-sm text-gray-600 dark:text-gray-400 p-2 bg-white dark:bg-card-darkAlt rounded border border-gray-200 dark:border-border-dark">
                  {ad.primary_text.length > 1 && <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Variation {i + 1}:</p>}
                  <RichMessageText text={text} uploadedMedia={conversationMedia} />
                </div>
              ))}
              {ad.primary_text.length > 2 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  +{ad.primary_text.length - 2} more variation{ad.primary_text.length - 2 > 1 ? 's' : ''}
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <RichMessageText text={ad.primary_text} uploadedMedia={conversationMedia} />
            </div>
          )}
        </div>
      )}

      {/* Descriptions */}
      {ad.descriptions && ad.descriptions.length > 0 && (
        <div className="mb-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Descriptions ({ad.descriptions.length}):
          </p>
          <div className="space-y-1">
            {ad.descriptions.slice(0, 2).map((desc: string, i: number) => (
              <div key={i} className="text-sm text-gray-600 dark:text-gray-400 pl-4 border-l-2 border-green-300">
                <RichMessageText text={desc} uploadedMedia={conversationMedia} />
              </div>
            ))}
            {ad.descriptions.length > 2 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 pl-4">
                +{ad.descriptions.length - 2} more descriptions
              </p>
            )}
          </div>
        </div>
      )}

      {/* Description (single) */}
      {ad.description && !ad.descriptions && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Description:</p>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <RichMessageText text={ad.description} uploadedMedia={conversationMedia} />
          </div>
        </div>
      )}

      {/* Call to Action */}
      {ad.call_to_action && (
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">CTA:</p>
          <Badge variant="green" size="sm">
            {ad.call_to_action}
          </Badge>
        </div>
      )}
    </div>
  );
};

export default GenericAdDisplay;
